import React, { useState, useEffect, useRef, useCallback } from 'react';
import MainLayout from '../user-dashboard/MainLayout';
import ReactPlayer from 'react-player';
import Quiz from './Quiz';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  fetchSessionStatusFast, 
  startSession, 
  updateSessionProgress, 
  completeSession,
  supabase
} from '../../supabase';

// SVG Check Mark Component
const CheckMarkSVG = ({ size = 16, color = "#374151" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="3" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

// Helper function to determine text color based on background
const getTextColorForBackground = (backgroundColor) => {
  if (backgroundColor === '#1B365D' || backgroundColor === '#00A0B0' || backgroundColor === '#4A90A4') {
    return '#FFFFFF';
  }
  return '#374151';
};

// SVG Components for Video Controls
const PlayButtonSVG = ({ size = 32, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="m380-300 280-180-280-180v360ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
  </svg>
);

const PauseButtonSVG = ({ size = 32, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M360-320h80v-320h-80v320Zm160 0h80v-320h-80v320ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
  </svg>
);

const RewindButtonSVG = ({ size = 24, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M440-240 200-480l240-240 56 56-183 184 183 184-56 56Zm264 0L464-480l240-240 56 56-183 184 183 184-56 56Z"/>
  </svg>
);

const ForwardButtonSVG = ({ size = 24, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M383-480 200-664l56-56 240 240-240 240-56-56 183-184Zm264 0L464-664l56-56 240 240-240 240-56-56 183-184Z"/>
  </svg>
);

const FullscreenSVG = ({ size = 16, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M200-200v-160h80v80h80v80H200Zm400 0v-80h80v-80h80v160H600ZM200-600v-160h160v80h-80v80h-80Zm480 0v-80h-80v-80h160v160h-80Z"/>
  </svg>
);

const LockSVG = ({ size = 16, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M240-80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h40v-80q0-83 58.5-141.5T480-920q83 0 141.5 58.5T680-720v80h40q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Zm0-80h480v-400H240v400Zm240-120q33 0 56.5-23.5T560-360q0-33-23.5-56.5T480-440q-33 0-56.5 23.5T400-360q0 33 23.5 56.5T480-280ZM360-640h240v-80q0-50-35-85t-85-35q-50 0-85 35t-35 85v80ZM240-160v-400 400Z"/>
  </svg>
);

const ExternalLinkSVG = ({ size = 16, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0 33-23.5 56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z"/>
  </svg>
);

// Video chapters data
const videoChapters = [
  {
    id: 1,
    title: "Introduction & Overview",
    description: "Session objectives and what you'll learn",
    timestamp: 6,
    duration: 29,
    color: "#1B365D",
    isSection: true,
    subsections: [
      { id: '1a', title: "Session Objectives", timestamp: 6, duration: 29 }
    ]
  },
  {
    id: 2,
    title: "Understanding Chronic Pain",
    description: "Learn what chronic orofacial pain is and how it differs from acute pain",
    timestamp: 35,
    duration: 205,
    color: "#00A0B0",
    isSection: true,
    subsections: [
      { id: '2a', title: "What is Chronic Orofacial Pain?", timestamp: 35, duration: 54 },
      { id: '2b', title: "Chronic vs. Acute Pain", timestamp: 89, duration: 151 }
    ]
  },
  {
    id: 3,
    title: "Mind-Body Connection",
    description: "Explore how pain affects both body and mind, and debunk common myths",
    timestamp: 240,
    duration: 228,
    color: "#4A90A4",
    isSection: true,
    subsections: [
      { id: '3a', title: "Pain in Body and Mind", timestamp: 240, duration: 33 },
      { id: '3b', title: "Debunking Pain Myths", timestamp: 273, duration: 195 }
    ]
  },
  {
    id: 4,
    title: "Practice & Techniques",
    description: "Hands-on exercises and deep breathing practice",
    timestamp: 468,
    duration: 564,
    color: "#7FCDCD",
    isSection: true,
    subsections: [
      { id: '4a', title: "Practice Introduction", timestamp: 468, duration: 9 },
      { id: '4b', title: "Mind-Body Exercise", timestamp: 477, duration: 166 },
      { id: '4c', title: "Deep Breathing Tool", timestamp: 643, duration: 77 },
      { id: '4d', title: "Guided Practice", timestamp: 720, duration: 312 }
    ]
  },
  {
    id: 5,
    title: "Session Summary",
    description: "Review key concepts and home practice assignment",
    timestamp: 1032,
    duration: 0,
    color: "#B8E6B8",
    isSection: true,
    subsections: [
      { id: '5a', title: "Key Takeaways", timestamp: 1032, duration: 0 }
    ]
  }
];

const ModernSessionPage = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [chaptersCollapsed, setChaptersCollapsed] = useState(true);

  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [showQuiz, setShowQuiz] = useState(null);
  const [isVideoCompleted, setIsVideoCompleted] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [userId, setUserId] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentSessionId, setCurrentSessionId] = useState('session1');
  const [currentSessionNumber, setCurrentSessionNumber] = useState(1);
  // const [assessmentUrls, setAssessmentUrls] = useState({
  //   postSessionUrl: ""
  // });

  // const [showPostAssessment, setShowPostAssessment] = useState(false);
  // const [postAssessmentClicked, setPostAssessmentClicked] = useState(false);
  // const [postAssessmentCompleted, setPostAssessmentCompleted] = useState(false);

  const [userProgress, setUserProgress] = useState({
    videoCompleted: false,
    quizCompleted: false,
    // postAssessmentCompleted: false
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(
    '/assets/FFWSession1Edited2_compressed.mp4',
  );
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [nextSessionScheduledTime, setNextSessionScheduledTime] =
    useState(null);
  const [scheduledCountdown, setScheduledCountdown] = useState(0);

  const [shouldRestorePosition, setShouldRestorePosition] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  const [hasStartedWatching, setHasStartedWatching] = useState(false);
  const [lastSavedPosition, setLastSavedPosition] = useState(0);
  const [rewatchCount, setRewatchCount] = useState(0);
  const [lastPracticeLogTime, setLastPracticeLogTime] = useState(0);

  const [currentChapter, setCurrentChapter] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(0.8);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [currentCaption, setCurrentCaption] = useState('');
  const [captions, setCaptions] = useState([]);
  const [overlayTimeout, setOverlayTimeout] = useState(null);

  const [activePracticeSession, setActivePracticeSession] = useState(null);
  const [completedFirstWatch, setCompletedFirstWatch] = useState(false);
  const [currentRewatch, setCurrentRewatch] = useState(null);

  const playerRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.id) {
      setUserId(user.id);
    }
  }, [user]);

  const DEEP_BREATHING_SECTION = {
    start: 643,
    end: 1032,
    name: 'Deep Breathing Practice',
  };

  const isInDeepBreathingSection = (position) => {
    return (
      position >= DEEP_BREATHING_SECTION.start &&
      position <= DEEP_BREATHING_SECTION.end
    );
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const getCurrentChapter = (seconds) => {
    for (let i = videoChapters.length - 1; i >= 0; i--) {
      if (seconds >= videoChapters[i].timestamp) {
        return i;
      }
    }
    return 0;
  };

  const getTimeUntilScheduledUnlock = useCallback((scheduledUnlockTime) => {
    if (!scheduledUnlockTime) return null;

    const now = new Date();
    const unlockTime = new Date(scheduledUnlockTime);
    const timeDiff = unlockTime.getTime() - now.getTime();

    if (timeDiff <= 0) return null;

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }, []);

  // const handlePostAssessmentClick = async () => {
  //   setPostAssessmentClicked(true);

  //   if (userId) {
  //     try {
  //       await updateSessionProgress(userId, currentSessionNumber, {
  //         post_assessment_link_clicked: true,
  //         post_assessment_clicked_timestamp: Date.now()
  //       });
  //       console.log("✅ Post-assessment link click recorded");
  //     } catch (error) {
  //       console.error("❌ Error recording post-assessment click:", error);
  //     }
  //   }

  //   window.open(assessmentUrls.postSessionUrl, '_blank');
  // };

  // const confirmPostAssessmentCompletion = async () => {
  //   console.log("Post-assessment completion confirmed");

  //   if (userId) {
  //     const success = await updateSessionProgress(userId, currentSessionNumber, {
  //       post_assessment_completed: true,
  //       post_assessment_completion_timestamp: Date.now(),
  //       session_fully_completed: true,
  //       updated_at: new Date().toISOString()
  //     });

  //     if (success) {
  //       setPostAssessmentCompleted(true);
  //       setUserProgress(prev => ({ ...prev, postAssessmentCompleted: true }));
  //       setShowPostAssessment(false);
  //       setSessionCompleted(true);

  //       const unlockDelay = 2 * 60 * 1000;
  //       setCountdown(unlockDelay / 1000);

  //       const nextSessionNumber = currentSessionNumber + 1;

  //       if (nextSessionNumber <= 5) {
  //         try {
  //           const { data: nextSessionSchedule } = await supabase
  //             .from('participant_session_schedule')
  //             .select(`session_${nextSessionNumber}_unlock_time`)
  //             .eq('user_id', userId)
  //             .single();

  //           if (nextSessionSchedule) {
  //             const scheduledTime = nextSessionSchedule[`session_${nextSessionNumber}_unlock_time`];
  //             if (scheduledTime) {
  //               const unlockTime = new Date(scheduledTime);
  //               const now = new Date();

  //               console.log(`📅 Next session ${nextSessionNumber} scheduled unlock:`, unlockTime);

  //               if (unlockTime > now) {
  //                 setNextSessionScheduledTime(unlockTime);
  //                 const timeRemaining = Math.max(0, (unlockTime.getTime() - now.getTime()) / 1000);
  //                 setScheduledCountdown(timeRemaining);
  //                 console.log(`⏰ Scheduled countdown set after completion: ${timeRemaining} seconds`);
  //               }
  //             }
  //           }
  //         } catch (error) {
  //           console.error('❌ Error fetching next session schedule after completion:', error);
  //         }
  //       }
  //     }
  //   }
  // };

  const handleFullscreen = () => {
    const videoContainer = playerRef.current?.wrapper?.parentElement;
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      videoContainer
        .requestFullscreen()
        .then(() => {
          setIsFullscreen(true);
        })
        .catch((err) => {
          console.log('Error attempting to enable fullscreen:', err);
        });
    } else {
      document
        .exitFullscreen()
        .then(() => {
          setIsFullscreen(false);
        })
        .catch((err) => {
          console.log('Error attempting to exit fullscreen:', err);
        });
    }
  };

  const handleCaptionToggle = () => {
    setShowCaptions(!showCaptions);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handlePlayPause = () => {
    const newPlayingState = !isPlaying;
    setIsPlaying(newPlayingState);

    if (newPlayingState && playedSeconds < 30 && hasStartedWatching && userId) {
      console.log('▶️ User restarted video - potential rewatch');
    }
  };

  const skipBackward = () => {
    if (playerRef.current && videoDuration > 0) {
      const newTime = Math.max(0, playedSeconds - 10);
      playerRef.current.seekTo(newTime, 'seconds');
      setPlayedSeconds(newTime);
      if (!isPlaying) {
        setIsPlaying(true);
      }
    }
  };

  const skipForward = (seconds = 10) => {
    if (playerRef.current && videoDuration > 0 && isVideoCompleted) {
      const newTime = Math.min(videoDuration - 5, playedSeconds + seconds);
      playerRef.current.seekTo(newTime, 'seconds');
      setPlayedSeconds(newTime);
      if (!isPlaying) {
        setIsPlaying(true);
      }
    }
  };

  const jumpToChapter = (timestamp, source = 'manual') => {
    if (playerRef.current) {
      const previousPosition = playedSeconds;

      // Allow jumping backward anytime, but only forward if video is completed
      if (timestamp <= playedSeconds || isVideoCompleted) {
        playerRef.current.seekTo(timestamp, 'seconds');

        if (isVideoCompleted && previousPosition > timestamp + 30) {
          console.log(
            `🔄 Chapter jump detected: ${previousPosition}s → ${timestamp}s`,
          );

          if (isInDeepBreathingSection(timestamp)) {
            console.log(
              `🧘 Jumped to deep breathing section - starting practice tracking`,
            );
            setActivePracticeSession({
              startTime: Date.now(),
              startPosition: timestamp,
              sessionType: 'chapter_jump',
            });
          }
        }

        if (!isPlaying) {
          setIsPlaying(true);
        }
      }
    }
  };

  const handleProgressBarClick = (e) => {
    if (videoDuration === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressPercentage = clickX / rect.width;
    const newTime = progressPercentage * videoDuration;

    // Allow seeking backward anytime, but only forward if video is completed
    if (newTime <= playedSeconds || isVideoCompleted) {
      if (playerRef.current) {
        playerRef.current.seekTo(newTime, 'seconds');
        setPlayedSeconds(newTime);
        if (!isPlaying) {
          setIsPlaying(true);
        }
      }
    }
  };

  const startRewatch = () => {
    console.log('🔄 Starting rewatch from beginning');

    const newRewatchData = {
      id: Date.now(),
      startTime: Date.now(),
      startPosition: 0,
      progress: 0,
      rewatchNumber: rewatchCount + 1,
    };

    setCurrentRewatch(newRewatchData);
    setRewatchCount((prev) => prev + 1);

    if (playerRef.current) {
      playerRef.current.seekTo(0, 'seconds');
      setPlayedSeconds(0);
      setIsPlaying(true);
    }

    console.log(`🔄 Started rewatch session #${newRewatchData.rewatchNumber}`);
  };

  const handleProgress = (progress) => {
    if (isRestoring) {
      return;
    }

    const currentPosition = progress.playedSeconds;

    if (!hasStartedWatching && currentPosition > 5) {
      setHasStartedWatching(true);
      console.log('▶️ User started watching video');
    }

    setPlayedSeconds(currentPosition);
    const newChapter = getCurrentChapter(currentPosition);
    if (newChapter !== currentChapter) {
      setCurrentChapter(newChapter);
    }

    const isNearEnd = videoDuration > 0 && videoDuration - currentPosition < 30;

    if (
      !isNearEnd &&
      Math.floor(currentPosition) % 3 === 0 &&
      currentPosition > 0
    ) {
      saveVideoProgress(currentPosition);
      setLastSavedPosition(currentPosition);
    }
  };

  const saveVideoProgress = async (currentSeconds) => {
    try {
      const currentUserId = userId;
      if (!currentUserId || videoDuration === 0 || currentSeconds < 5) return;

      const progressPercentage = Math.round(
        (currentSeconds / videoDuration) * 100,
      );

      if (progressPercentage >= 95) {
        return;
      }

      const progressData = {
        video_progress_percentage: progressPercentage,
        last_video_position: currentSeconds,
        updated_at: new Date().toISOString(),
      };

      await updateSessionProgress(
        currentUserId,
        currentSessionNumber,
        progressData,
      );
    } catch (error) {
      console.error('❌ Error saving video progress:', error);
    }
  };

  const handleVideoEnd = async () => {
    console.log('🎬 Video ended - marking as completed');
    setIsPlaying(false);
    setIsVideoCompleted(true);
    setCompletedFirstWatch(true);

    setUserProgress((prev) => ({
      ...prev,
      videoCompleted: true,
    }));

    if (activePracticeSession) {
      setActivePracticeSession(null);
    }

    if (currentRewatch) {
      setCurrentRewatch(null);
    }

    setShouldRestorePosition(null);

    try {
      const currentUserId = userId;
      if (!currentUserId) return;

      console.log('🎬 Video completed - saving final progress');

      await updateSessionProgress(currentUserId, currentSessionNumber, {
        video_watched: true,
        video_completed: true,
        video_progress_percentage: 100,
        video_watch_time_seconds: Math.floor(videoDuration),
        last_video_position: 0,
        video_watched_timestamp: Date.now(),
        video_completion_date: new Date().toISOString().split('T')[0],
      });

      console.log('✅ Video completion saved to Supabase');
    } catch (error) {
      console.error('❌ Error updating video completion:', error);
    }
  };

  const calculateProgress = () => {
    const steps = 2;
    let completed = 0;

    if (userProgress.videoCompleted) completed++;
    if (userProgress.quizCompleted) completed++;
   // if (userProgress.postAssessmentCompleted) completed++;

    return Math.round((completed / steps) * 100);
  };

  const navigateToNextSession = () => {
    const nextSessionNumber = currentSessionNumber + 1;
    window.location.href = `/session${nextSessionNumber.toString().padStart(2, '0')}`;
  };

  const handleSessionStart = async (userId, sessionNumber) => {
    try {
      console.log(`🚀 Starting session ${sessionNumber} for user ${userId}`);

      const success = await startSession(userId, sessionNumber);

      if (success) {
        console.log(`✅ Session ${sessionNumber} started successfully`);

        await updateSessionProgress(userId, sessionNumber, {
          session_started: true,
          session_start_time: Date.now(),
          video_started: true,
          video_start_time: Date.now(),
          video_progress_percentage: 0,
          video_watch_time_seconds: 0,
        });

        return true;
      } else {
        console.error(`❌ Failed to start session ${sessionNumber}`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Error starting session: ${error.message}`);
      return false;
    }
  };

  const handleLessonCompletion = async (quizData) => {
    try {
      const currentUserId = userId;
      if (!currentUserId) return;

      console.log(`🎯 Quiz completed for session ${currentSessionNumber}`);

      const completionData = {
        video_watched: true,
        video_completed: true,
        video_progress_percentage: 100,
        quiz_completed: true,
        quiz_score: quizData?.score || 5,
        quiz_answers: quizData?.answers || [],
        quiz_attempts: quizData?.attempts || 1,
        quiz_time_taken_seconds: quizData?.timeTaken || 0,
      };

      const result = await updateSessionProgress(
        currentUserId,
        currentSessionNumber,
        completionData,
      );

      if (result !== null && result !== false) {
        console.log(`✅ Quiz completed for session ${currentSessionNumber}`);

        setQuizCompleted(true);
        setIsVideoCompleted(true);
        setShowQuiz(null);
        setSessionCompleted(true);

        setUserProgress((prev) => ({
          ...prev,
          videoCompleted: true,
          quizCompleted: true,
        }));

        // setPostAssessmentClicked(false);
        // setPostAssessmentCompleted(false);
      } else {
        console.error('❌ Failed to record quiz completion');
        alert('Error completing quiz. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error completing quiz:', error);
      alert('Error completing quiz. Please try again.');
    }
  };

  const fetchSessionProgress = async (userId) => {
    if (!userId) return;

    try {
      setLoading(true);

      console.log(
        `📊 Fetching session ${currentSessionNumber} status for user ${userId}`,
      );

      const sessionStatus = await fetchSessionStatusFast(
        userId,
        currentSessionNumber,
      );

      console.log('✅ Full session status received:', sessionStatus);

      setSessionCompleted(sessionStatus.sessionFullyCompleted || false);
      setIsVideoCompleted(sessionStatus.videoWatched);
      setQuizCompleted(sessionStatus.quizCompleted);

      // setPostAssessmentClicked(sessionStatus.postAssessmentLinkClicked || false);
      // setPostAssessmentCompleted(sessionStatus.postAssessmentCompleted || false);

      setUserProgress({
        videoCompleted:
          sessionStatus.videoWatched || sessionStatus.videoCompleted,
        quizCompleted: sessionStatus.quizCompleted,
        //  postAssessmentCompleted: sessionStatus.postAssessmentCompleted || false
      });

      if (sessionStatus.videoWatched) {
        setCompletedFirstWatch(true);
        console.log(
          '📹 Video was previously completed - preserving completion status',
        );
      }

      const shouldShowMainSession =
        sessionStatus.sessionStarted ||
        sessionStatus.videoWatched ||
        sessionStatus.quizCompleted ||
        sessionStatus.sessionFullyCompleted;
      setSessionStarted(shouldShowMainSession);

      let positionToRestore = null;

      if (
        sessionStatus.lastVideoPosition &&
        sessionStatus.lastVideoPosition > 10
      ) {
        const lastPosition = sessionStatus.lastVideoPosition;
        const videoLength = 1166;

        if (lastPosition < videoLength - 30) {
          positionToRestore = lastPosition;
          console.log(`📹 Setting positionToRestore: ${positionToRestore}s`);
        }
      }

      if (positionToRestore) {
        setShouldRestorePosition(positionToRestore);
        setPlayedSeconds(positionToRestore);
        console.log(`📹 Will restore to position: ${positionToRestore}s`);
      } else {
        console.log('📹 No restoration needed - starting from beginning');
        setShouldRestorePosition(null);
        setPlayedSeconds(0);
      }

      if (
        sessionStatus.nextSessionUnlockTime &&
        sessionStatus.nextSessionUnlockTime > Date.now()
      ) {
        const timeRemaining = Math.max(
          0,
          (sessionStatus.nextSessionUnlockTime - Date.now()) / 1000,
        );
        setCountdown(timeRemaining);
      } else {
        setCountdown(0);
      }

      if (sessionStatus.sessionFullyCompleted) {
        console.log('🔍 Session completed, checking next session schedule...');

        const nextSessionNumber = currentSessionNumber + 1;

        if (nextSessionNumber <= 5) {
          try {
            const { data: nextSessionSchedule } = await supabase
              .from('participant_session_schedule')
              .select(`session_${nextSessionNumber}_unlock_time`)
              .eq('user_id', userId)
              .single();

            if (nextSessionSchedule) {
              const scheduledTime =
                nextSessionSchedule[`session_${nextSessionNumber}_unlock_time`];
              if (scheduledTime) {
                const unlockTime = new Date(scheduledTime);
                const now = new Date();

                console.log(
                  `📅 Next session ${nextSessionNumber} scheduled unlock:`,
                  unlockTime,
                );

                if (unlockTime > now) {
                  setNextSessionScheduledTime(unlockTime);
                  const timeRemaining = Math.max(
                    0,
                    (unlockTime.getTime() - now.getTime()) / 1000,
                  );
                  setScheduledCountdown(timeRemaining);
                  console.log(
                    `⏰ Scheduled countdown set: ${timeRemaining} seconds`,
                  );
                }
              }
            }
          } catch (error) {
            console.error('❌ Error fetching next session schedule:', error);
          }
        }
      }
    } catch (error) {
      console.error('❌ Error fetching session progress:', error);
      setSessionStarted(false);
      setIsVideoCompleted(false);
      setQuizCompleted(false);
      setSessionCompleted(false);
      setPlayedSeconds(0);
      setShouldRestorePosition(null);
      setUserProgress({
        videoCompleted: false,
        quizCompleted: false,
       // postAssessmentCompleted: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchSessionProgress(userId);
    }
  }, [userId, currentSessionNumber]);

  useEffect(() => {
    const loadCaptions = async () => {
      try {
        const response = await fetch('/assets/vtt/FFWSession1Captions.vtt');
        const vttText = await response.text();

        const lines = vttText.split('\n');
        const parsedCaptions = [];

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();

          if (line.includes('-->')) {
            const [startStr, endStr] = line.split('-->').map((s) => s.trim());

            const parseTime = (timeStr) => {
              const parts = timeStr.split(':');
              const hours = parseInt(parts[0]) || 0;
              const minutes = parseInt(parts[1]) || 0;
              const seconds = parseFloat(parts[2]) || 0;
              return hours * 3600 + minutes * 60 + seconds;
            };

            const start = parseTime(startStr);
            const end = parseTime(endStr);

            let captionText = '';
            for (let j = i + 1; j < lines.length; j++) {
              const textLine = lines[j].trim();
              if (textLine === '' || textLine.includes('-->')) break;
              if (!textLine.match(/^\d+$/)) {
                captionText += (captionText ? ' ' : '') + textLine;
              }
            }

            if (captionText) {
              parsedCaptions.push({ start, end, text: captionText });
            }
          }
        }

        setCaptions(parsedCaptions);
        console.log(
          '✅ Loaded',
          parsedCaptions.length,
          'captions for Session 1',
        );
      } catch (error) {
        console.error('Error loading captions:', error);
      }
    };

    loadCaptions();
  }, []);

  useEffect(() => {
    if (!showCaptions || captions.length === 0) {
      setCurrentCaption('');
      return;
    }

    const currentCue = captions.find(
      (caption) =>
        playedSeconds >= caption.start && playedSeconds < caption.end,
    );

    setCurrentCaption(currentCue ? currentCue.text : '');
  }, [playedSeconds, captions, showCaptions]);

  // useEffect(() => {
  //   const loadAssessmentUrls = async () => {
  //     if (user?.id && currentSessionNumber) {
  //       console.log(
  //         `Loading assessment URLs for user ${user.id}, session ${currentSessionNumber}`,
  //       );

  //       try {
  //         const { data, error } = await supabase
  //           .from('participant_assessments')
  //           .select('post_assessment_url')
  //           .eq('user_id', user.id)
  //           .eq('session_number', currentSessionNumber)
  //           .single();

  //         if (!error && data) {
  //           console.log('Found session-specific assessment URLs:', data);
  //           setAssessmentUrls({
  //             postSessionUrl:
  //               data.post_assessment_url ||
  //               'https://example.com/post-session-assessment',
  //           });
  //         }
  //       } catch (error) {
  //         console.error('Error loading assessment URLs:', error);
  //       }
  //     }
  //   };
  //   loadAssessmentUrls();
  // }, [user, currentSessionNumber]);

  useEffect(() => {
    if (countdown > 0) {
      const interval = setInterval(() => {
        setCountdown((prev) => {
          const newValue = Math.max(0, prev - 1);
          if (prev > 0 && newValue === 0) {
            if (userId) {
              fetchSessionProgress(userId);
            }
          }
          return newValue;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [countdown, userId]);

  useEffect(() => {
    if (scheduledCountdown > 0) {
      const interval = setInterval(() => {
        setScheduledCountdown((prev) => {
          const newValue = Math.max(0, prev - 1);
          if (prev > 0 && newValue === 0) {
            console.log(
              '⏰ Scheduled countdown reached zero, next session should be unlocked',
            );
            setNextSessionScheduledTime(null);
          }
          return newValue;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [scheduledCountdown]);

  if (loading || authLoading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#F8F9FA',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <MainLayout>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  border: '3px solid #f1f3f4',
                  borderTop: '3px solid #1B365D',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px',
                }}
              />
              <p
                style={{
                  color: '#374151',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontSize: '16px',
                  fontWeight: '400',
                }}
              >
                Loading Session {currentSessionNumber}...
              </p>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  if (!sessionStarted && !sessionCompleted && !loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#F8F9FA',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <MainLayout>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              maxWidth: '1000px',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <div
              style={{
                padding: isMobile ? '24px 20px 0 20px' : '64px 40px 0 40px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '16px',
                }}
              >
                {isMobile && (
                  <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #E5E7EB',
                      flexShrink: 0,
                      cursor: 'pointer',
                      fontSize: '20px',
                      color: '#374151',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    ←
                  </button>
                )}
                <div
                  style={{
                    backgroundColor: '#1B365D',
                    color: '#FFFFFF',
                    borderRadius: '12px',
                    padding: isMobile ? '8px 16px' : '12px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    minWidth: '90px',
                    textAlign: 'center',
                    border: '1px solid #E5E7EB',
                    flexShrink: 0,
                    height: isMobile ? '32px' : '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  }}
                >
                  Session {currentSessionNumber}
                </div>

                <div
                  style={{
                    backgroundColor: '#F3F4F6',
                    borderRadius: '12px',
                    padding: isMobile ? '8px 12px' : '12px 16px',
                    border: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    height: isMobile ? '32px' : '44px',
                  }}
                >
                  <span
                    style={{
                      fontSize: isMobile ? '12px' : '14px',
                      fontWeight: '600',
                      color: '#6B7280',
                    }}
                  >
                    Not Started
                  </span>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: isMobile ? '20px 24px' : '32px 40px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  textAlign: 'center',
                  marginBottom: '20px',
                }}
              >
                <h1
                  style={{
                    fontSize: isMobile ? '24px' : '36px',
                    fontWeight: '600',
                    color: '#1B365D',
                    margin: 0,
                    letterSpacing: '-1px',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    lineHeight: '1.2',
                  }}
                >
                  Taking Charge of Orofacial Pain
                </h1>
              </div>
            </div>

            <div
              style={{
                flex: 1,
                padding: isMobile ? '0 20px 20px 20px' : '0 40px 40px 40px',
              }}
            >
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  border: '1px solid #E5E7EB',
                  padding: isMobile ? '24px' : '32px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                }}
              >
                <h2
                  style={{
                    fontSize: isMobile ? '20px' : '26px',
                    fontWeight: '600',
                    color: '#374151',
                    margin: '0 0 16px 0',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                >
                  Welcome to Session {currentSessionNumber}
                </h2>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginBottom: '20px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1B365D',
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      borderRadius: '20px',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: '500',
                      border: '1px solid #3B82F6',
                    }}
                  >
                    Understanding Pain
                  </div>
                  <div
                    style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1B365D',
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      borderRadius: '20px',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: '500',
                      border: '1px solid #3B82F6',
                    }}
                  >
                    Mind-Body Connection
                  </div>
                  <div
                    style={{
                      backgroundColor: '#EFF6FF',
                      color: '#1B365D',
                      padding: isMobile ? '6px 12px' : '8px 16px',
                      borderRadius: '20px',
                      fontSize: isMobile ? '11px' : '12px',
                      fontWeight: '500',
                      border: '1px solid #3B82F6',
                    }}
                  >
                    Deep Breathing
                  </div>
                </div>

                <p
                  style={{
                    fontSize: isMobile ? '14px' : '16px',
                    color: '#6B7280',
                    margin: '0 0 32px 0',
                    lineHeight: '1.6',
                    maxWidth: '600px',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                >
                  Learn to understand chronic orofacial pain, how it differs
                  from acute pain, and discover the powerful connection between
                  your mind and body. You'll explore common myths about pain and
                  practice deep breathing techniques to help manage your
                  symptoms.
                </p>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
                    gap: '16px',
                    marginBottom: '32px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: '#EFF6FF',
                      borderRadius: '12px',
                      border: '1px solid #3B82F6',
                      padding: '16px',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="#1B365D"
                      >
                        <path d="m380-340 280-180-280-180v360Zm-60 220v-80H160q-33 0-56.5-23.5T80-280v-480q0-33 23.5-56.5T160-840h640q33 0 56.5 23.5T880-760v480q0 33-23.5 56.5T800-200H640v80H320ZM160-280h640v-480H160v480Zm0 0v-480 480Z" />
                      </svg>
                    </div>
                    <h4
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#1B365D',
                        margin: '0 0 4px 0',
                      }}
                    >
                      Interactive Video
                    </h4>
                    <p
                      style={{
                        fontSize: '11px',
                        color: '#1B365D',
                        margin: 0,
                        opacity: 0.7,
                      }}
                    >
                      ~20 minutes
                    </p>
                  </div>

                  <div
                    style={{
                      backgroundColor: '#F0F9FF',
                      borderRadius: '12px',
                      border: '1px solid #E0E7FF',
                      padding: '16px',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="20px"
                        viewBox="0 -960 960 960"
                        width="20px"
                        fill="#374151"
                      >
                        <path d="M560-360q17 0 29.5-12.5T602-402q0-17-12.5-29.5T560-444q-17 0-29.5 12.5T518-402q0 17 12.5 29.5T560-360Zm-30-128h60q0-29 6-42.5t28-35.5q30-30 40-48.5t10-43.5q0-45-31.5-73.5T560-760q-41 0-71.5 23T446-676l54 22q9-25 24.5-37.5T560-704q24 0 39 13.5t15 36.5q0 14-8 26.5T578-596q-33 29-40.5 45.5T530-488ZM320-240q-33 0-56.5-23.5T240-320v-480q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H320Zm0-80h480v-480H320v480ZM160-80q-33 0-56.5-23.5T80-160v-560h80v560h560v80H160Zm160-720v480-480Z" />
                      </svg>
                    </div>
                    <h4
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#374151',
                        margin: '0 0 4px 0',
                      }}
                    >
                      Knowledge Check
                    </h4>
                    <p
                      style={{
                        fontSize: '11px',
                        color: '#6B7280',
                        margin: 0,
                      }}
                    >
                      Quiz & reflection
                    </p>
                  </div>
                </div> 

                <button
                  onClick={async () => {
                    if (userId && currentSessionNumber) {
                      const sessionStarted = await handleSessionStart(
                        userId,
                        currentSessionNumber,
                      );
                      if (sessionStarted) {
                        setSessionStarted(true);
                      }
                    } else {
                      setSessionStarted(true);
                    }
                  }}
                  style={{
                    backgroundColor: '#1B365D',
                    color: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '24px',
                    padding: isMobile ? '12px 24px' : '16px 32px',
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                  }}
                >
                  Start Session
                </button>
              </div>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#F8F9FA',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <MainLayout>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          <div
            style={{
              padding: isMobile ? '24px 20px' : '32px 40px',
              backgroundColor: '#F8F9FA',
            }}
          >
            <div
              style={{
                maxWidth: '1400px',
                margin: '0 auto',
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: isMobile ? '16px' : '20px',
                }}
              >
                {isMobile ? (
                  <button
                    onClick={() => navigate('/sessions')}
                    style={{
                      width: '48px',
                      height: '48px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #E5E7EB',
                      flexShrink: 0,
                      cursor: 'pointer',
                      fontSize: '20px',
                      color: '#374151',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    ←
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 0',
                    }}
                  >
                    ← Back to Dashboard
                  </button>
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? '16px' : '24px',
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '24px',
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: '#1B365D',
                        color: '#FFFFFF',
                        borderRadius: '8px',
                        padding: '4px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        border: '1px solid #E5E7EB',
                      }}
                    >
                      SESSION {currentSessionNumber}
                    </div>
                    <div
                      style={{
                        backgroundColor: sessionCompleted
                          ? '#ECFDF5'
                          : '#F3F4F6',
                        borderRadius: '8px',
                        padding: '4px 12px',
                        border: '1px solid #E5E7EB',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: sessionCompleted ? '#10B981' : '#6B7280',
                        }}
                      >
                        {sessionCompleted
                          ? 'Complete'
                          : isVideoCompleted && quizCompleted
                            ? 'Quiz Complete'
                            : isVideoCompleted
                              ? 'Video Complete'
                              : 'In Progress'}
                      </span>
                      {sessionCompleted && (
                        <CheckMarkSVG size={10} color="#10B981" />
                      )}
                    </div>
                  </div>

                  <h1
                    style={{
                      fontSize: isMobile ? '24px' : '36px',
                      fontWeight: '600',
                      color: '#1B365D',
                      margin: '0 0 8px 0',
                      letterSpacing: '-1px',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      lineHeight: '1.2',
                    }}
                  >
                    Taking Charge of Orofacial Pain
                  </h1>

                  <p
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                      color: '#6B7280',
                      margin: 0,
                      lineHeight: '1.4',
                    }}
                  >
                    Understanding chronic pain and practicing deep breathing
                    techniques
                  </p>
                </div>

                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '12px',
                    padding: isMobile ? '20px' : '24px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    minWidth: isMobile ? '100%' : '320px',
                    textAlign: 'left',
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '12px',
                    }}
                  >
                    {calculateProgress() === 100
                      ? 'Session Complete!'
                      : calculateProgress() >= 75
                        ? 'Almost There!'
                        : calculateProgress() > 0
                          ? 'Making Progress!'
                          : 'Ready to Start!'}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    {[
                      { name: 'Video', completed: userProgress.videoCompleted },
                      { name: 'Quiz', completed: userProgress.quizCompleted }
                    ].map((item, index) => (
                      <React.Fragment key={index}>
                        <div
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            backgroundColor: item.completed
                              ? '#10B981'
                              : '#F3F4F6',
                            border: '2px solid',
                            borderColor: item.completed ? '#10B981' : '#D1D5DB',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {item.completed && (
                            <CheckMarkSVG size={12} color="#FFFFFF" />
                          )}
                        </div>

                        {index < 1 && (
                          <div
                            style={{
                              flex: 1,
                              height: '2px',
                              backgroundColor: userProgress[
                                [
                                  'videoCompleted',
                                  'quizCompleted',
                               //   'postAssessmentCompleted',
                                ][index + 1]
                              ]
                                ? '#10B981'
                                : '#D1D5DB',
                              margin: '0 8px',
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '10px',
                      color: '#6B7280',
                      fontWeight: '500',
                    }}
                  >
                    <span style={{ width: '24px', textAlign: 'center' }}>
                      Video
                    </span>
                    <span style={{ width: '24px', textAlign: 'center' }}>
                      Quiz
                    </span>
                    {/* <span style={{ width: '24px', textAlign: 'center' }}>
                      Post
                    </span> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: isMobile ? '0 20px 16px 20px' : '0 40px 24px 40px',
            }}
          >
            <div
              style={{
                maxWidth: '1400px',
                margin: '0 auto',
                width: '100%',
              }}
            >
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '16px',
                  padding: isMobile ? '16px 20px' : '20px 24px',
                  border: '1px solid #E5E7EB',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  textAlign: 'center',
                }}
              >
                {sessionCompleted
                  ? "Excellent! You've completed all session components."
                  : !isVideoCompleted
                    ? 'Watch the video to learn about chronic orofacial pain management.'
                    : !quizCompleted
                      ? 'Complete the quiz to test your understanding.'
                      : "Complete the quiz to finish this session."}
              </div>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              padding: isMobile ? '0 20px 20px 20px' : '0 40px 40px 40px',
              maxWidth: '1475px',
              margin: '0 auto',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
                gap: '32px',
              }}
            >
              <div
                style={{
                  order: isMobile ? 1 : 1,
                }}
              >
                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden',
                    marginBottom: '24px',
                    padding: '24px',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      backgroundColor: '#000',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      border: '1px solid #E5E7EB',
                    }}
                  >
                    <ReactPlayer
                      ref={playerRef}
                      url={currentVideo}
                      playing={isPlaying}
                      controls={false}
                      onProgress={handleProgress}
                      onDuration={(duration) => {
                        console.log(
                          `📹 Video duration loaded: ${Math.floor(duration)} seconds`,
                        );
                        setVideoDuration(duration);

                        if (
                          shouldRestorePosition !== null &&
                          playerRef.current &&
                          duration > 0
                        ) {
                          console.log(
                            `📹 Restoring video position to: ${shouldRestorePosition}s`,
                          );
                          setIsRestoring(true);

                          setTimeout(() => {
                            if (playerRef.current) {
                              playerRef.current.seekTo(
                                shouldRestorePosition,
                                'seconds',
                              );
                              setPlayedSeconds(shouldRestorePosition);

                              const restoredChapter = getCurrentChapter(
                                shouldRestorePosition,
                              );
                              setCurrentChapter(restoredChapter);

                              setTimeout(() => {
                                setIsRestoring(false);
                                setShouldRestorePosition(null);
                              }, 500);
                            }
                          }, 100);
                        }
                      }}
                      onEnded={handleVideoEnd}
                      playbackRate={playbackRate}
                      volume={volume}
                      width="100%"
                      height="auto"
                      style={{ aspectRatio: '16/9' }}
                      config={{
                        file: {
                          attributes: {
                            crossOrigin: 'anonymous',
                          },
                        },
                      }}
                      onReady={() => {
                        if (playerRef.current) {
                          const internalPlayer =
                            playerRef.current.getInternalPlayer();
                          if (internalPlayer) {
                            if (internalPlayer.textTracks) {
                              const textTracks = internalPlayer.textTracks;
                              for (let i = 0; i < textTracks.length; i++) {
                                textTracks[i].mode = 'disabled';
                              }
                            }
                            const tracks =
                              internalPlayer.querySelectorAll('track');
                            tracks.forEach((track) => track.remove());
                          }
                        }
                      }}
                    />
                    <div
                      ref={overlayRef}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '40px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'auto',
                        background: 'rgba(0, 0, 0, 0.4)',
                      }}
                      onMouseEnter={() => {
                        if (
                          !isVideoCompleted ||
                          playedSeconds < videoDuration - 5
                        ) {
                          if (overlayRef.current) {
                            overlayRef.current.style.opacity = '1';
                          }

                          if (overlayTimeout) {
                            clearTimeout(overlayTimeout);
                            setOverlayTimeout(null);
                          }

                          const timeout = setTimeout(() => {
                            if (overlayRef.current) {
                              overlayRef.current.style.opacity = '0';
                            }
                          }, 3000);

                          setOverlayTimeout(timeout);
                        }
                      }}
                      onMouseMove={() => {
                        if (
                          !isVideoCompleted ||
                          playedSeconds < videoDuration - 5
                        ) {
                          if (overlayRef.current) {
                            overlayRef.current.style.opacity = '1';
                          }

                          if (overlayTimeout) {
                            clearTimeout(overlayTimeout);
                          }

                          const timeout = setTimeout(() => {
                            if (overlayRef.current) {
                              overlayRef.current.style.opacity = '0';
                            }
                          }, 3000);

                          setOverlayTimeout(timeout);
                        }
                      }}
                      onMouseLeave={() => {
                        // FIXED - removed (e) and changed to overlayRef.current
                        if (
                          !isVideoCompleted ||
                          playedSeconds < videoDuration - 5
                        ) {
                          if (overlayRef.current) {
                            overlayRef.current.style.opacity = '0';
                          }

                          if (overlayTimeout) {
                            clearTimeout(overlayTimeout);
                            setOverlayTimeout(null);
                          }
                        }
                      }}
                    >
                      {isVideoCompleted &&
                      playedSeconds >= videoDuration - 5 ? (
                        <div
                          style={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: '12px',
                            padding: '24px',
                            textAlign: 'center',
                            border: '1px solid #E5E7EB',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            opacity: 1,
                          }}
                        >
                          <h4
                            style={{
                              fontSize: '18px',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 8px 0',
                            }}
                          >
                            Video Complete!
                          </h4>
                          <p
                            style={{
                              fontSize: '14px',
                              color: '#6B7280',
                              margin: '0 0 16px 0',
                            }}
                          >
                            You can rewatch this video anytime
                          </p>
                          <button
                            onClick={startRewatch}
                            style={{
                              backgroundColor: '#1B365D',
                              color: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '24px',
                              padding: '12px 20px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              fontFamily:
                                'system-ui, -apple-system, sans-serif',
                            }}
                          >
                            Rewatch Video
                          </button>
                        </div>
                      ) : (
                        <>
                          {/* Skip backward - now available for everyone */}
                          <button
                            onClick={skipBackward}
                            style={{
                              width: '64px',
                              height: '64px',
                              borderRadius: '50%',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              color: '#374151',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                              fontSize: '12px',
                              fontWeight: '600',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                          >
                            <RewindButtonSVG size={24} color="#374151" />
                            <div style={{ fontSize: '10px', marginTop: '2px' }}>
                              10s
                            </div>
                          </button>

                          <button
                            onClick={handlePlayPause}
                            style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              color: '#374151',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            }}
                          >
                            {isPlaying ? (
                              <PauseButtonSVG size={32} color="#374151" />
                            ) : (
                              <PlayButtonSVG size={32} color="#374151" />
                            )}
                          </button>

                          {isVideoCompleted && (
                            <button
                              onClick={() => skipForward()}
                              style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                color: '#374151',
                                cursor: 'pointer',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                fontSize: '12px',
                                fontWeight: '600',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                              }}
                            >
                              <ForwardButtonSVG size={24} color="#374151" />
                              <div
                                style={{ fontSize: '10px', marginTop: '2px' }}
                              >
                                10s
                              </div>
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Fullscreen captions */}
                    {isFullscreen && showCaptions && currentCaption && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '80px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.85)',
                          color: '#FFFFFF',
                          padding: '16px 32px',
                          borderRadius: '8px',
                          fontSize: '20px',
                          lineHeight: '1.5',
                          textAlign: 'center',
                          maxWidth: '80%',
                          zIndex: 10,
                          pointerEvents: 'none',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
                        }}
                      >
                        {currentCaption}
                      </div>
                    )}
                  </div>

                  {/* Normal view captions - ADD !isFullscreen */}
                  {!isFullscreen && showCaptions && currentCaption && (
                    <div
                      style={{
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        color: '#FFFFFF',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        textAlign: 'center',
                        marginBottom: '16px',
                        minHeight: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                      }}
                    >
                      {currentCaption}
                    </div>
                  )}

                  <div style={{ padding: '20px', backgroundColor: '#FFFFFF' }}>
                    <div
                      style={{
                        height: '8px',
                        backgroundColor: '#F3F4F6',
                        borderRadius: '4px',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: isVideoCompleted ? 'pointer' : 'default',
                        marginBottom: '16px',
                        border: '1px solid #E5E7EB',
                      }}
                      onClick={handleProgressBarClick}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${(playedSeconds / videoDuration) * 100}%`,
                          backgroundColor: '#1B365D',
                          borderRadius: '4px',
                          transition: 'width 0.1s ease',
                        }}
                      />

                      {videoChapters.map((chapter, index) => {
                        // Can access if: video is completed OR chapter is at/before current position
                        const canAccess =
                          isVideoCompleted ||
                          chapter.timestamp <= playedSeconds;
                        return (
                          <div
                            key={chapter.id}
                            style={{
                              position: 'absolute',
                              left: `${(chapter.timestamp / videoDuration) * 100}%`,
                              top: '-2px',
                              width: '3px',
                              height: '12px',
                              backgroundColor: chapter.color,
                              borderRadius: '2px',
                              transform: 'translateX(-50%)',
                              cursor: canAccess ? 'pointer' : 'not-allowed',
                              opacity: canAccess ? 1 : 0.5,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (canAccess) {
                                jumpToChapter(
                                  chapter.timestamp,
                                  'progress-marker',
                                );
                              }
                            }}
                          />
                        );
                      })}
                    </div>

                    {!isMobile && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '16px',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <button
                            onClick={handlePlayPause}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              backgroundColor: '#1B365D',
                              border: '1px solid #E5E7EB',
                              color: '#FFFFFF',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {isPlaying ? (
                              <PauseButtonSVG size={16} color="#FFFFFF" />
                            ) : (
                              <PlayButtonSVG size={16} color="#FFFFFF" />
                            )}
                          </button>

                          {/* Skip backward - now available for everyone */}
                          <button
                            onClick={skipBackward}
                            style={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '20px',
                              color: '#374151',
                              cursor: 'pointer',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <RewindButtonSVG size={12} color="#374151" />
                            10s
                          </button>

                          {isVideoCompleted && (
                            <button
                              onClick={() => skipForward()}
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '20px',
                                color: '#374151',
                                cursor: 'pointer',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                            >
                              10s
                              <ForwardButtonSVG size={12} color="#374151" />
                            </button>
                          )}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '14px',
                              color: '#374151',
                              fontFamily: 'monospace',
                            }}
                          >
                            {formatTime(playedSeconds)} /{' '}
                            {formatTime(videoDuration)}
                          </span>

                          {/* Volume Control - Vertical Popup */}
                          <div
                            style={{
                              position: 'relative',
                            }}
                            onMouseEnter={() =>
                              !isMobile && setShowVolumeSlider(true)
                            }
                            onMouseLeave={() =>
                              !isMobile && setShowVolumeSlider(false)
                            }
                          >
                            <button
                              onClick={() =>
                                isMobile &&
                                setShowVolumeSlider(!showVolumeSlider)
                              }
                              style={{
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                color: '#374151',
                                cursor: 'pointer',
                                padding: '8px',
                                fontSize: '12px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              title={`Volume: ${Math.round(volume * 100)}%`}
                            >
                              <svg
                                width="16"
                                height="16"
                                fill="#374151"
                                viewBox="0 0 24 24"
                              >
                                {volume === 0 ? (
                                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                                ) : volume < 0.5 ? (
                                  <path d="M7 9v6h4l5 5V4l-5 5H7z" />
                                ) : (
                                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
                                )}
                              </svg>
                            </button>

                            {showVolumeSlider && (
                              <div
                                style={{
                                  position: 'absolute',
                                  bottom: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginBottom: '8px',
                                  backgroundColor: '#FFFFFF',
                                  border: '1px solid #E5E7EB',
                                  borderRadius: '8px',
                                  padding: '16px 12px',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                  zIndex: 10,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  width: '48px',
                                }}
                              >
                                <input
                                  type="range"
                                  min="0"
                                  max="1"
                                  step="0.05"
                                  value={volume}
                                  onChange={(e) =>
                                    setVolume(parseFloat(e.target.value))
                                  }
                                  orient="vertical"
                                  style={{
                                    WebkitAppearance: 'slider-vertical',
                                    width: '4px',
                                    height: '100px',
                                    cursor: 'pointer',
                                    background: `linear-gradient(to top, #3B82F6 ${volume * 100}%, #E5E7EB ${volume * 100}%)`,
                                    borderRadius: '2px',
                                    outline: 'none',
                                  }}
                                />
                                <div
                                  style={{
                                    textAlign: 'center',
                                    fontSize: '11px',
                                    color: '#6B7280',
                                    fontWeight: '600',
                                    marginTop: '12px',
                                  }}
                                >
                                  {Math.round(volume * 100)}%
                                </div>
                              </div>
                            )}
                          </div>

                          <button
                            onClick={handleCaptionToggle}
                            style={{
                              backgroundColor: showCaptions
                                ? '#1B365D'
                                : '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              color: showCaptions ? '#FFFFFF' : '#374151',
                              cursor: 'pointer',
                              padding: '8px',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s ease',
                            }}
                            title={
                              showCaptions ? 'Hide Captions' : 'Show Captions'
                            }
                          >
                            <svg
                              width="16"
                              height="16"
                              fill="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z" />
                            </svg>
                          </button>

                          <button
                            onClick={handleFullscreen}
                            style={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              color: '#374151',
                              cursor: 'pointer',
                              padding: '8px',
                              fontSize: '12px',
                              fontWeight: '500',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                            title="Toggle Fullscreen"
                          >
                            <FullscreenSVG size={16} color="#374151" />
                          </button>
                        </div>
                      </div>
                    )}

                    {isVideoCompleted && (
                      <div
                        style={{
                          marginTop: '16px',
                          backgroundColor: '#ECFDF5',
                          color: '#10B981',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          fontSize: '14px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          border: '1px solid #10B981',
                        }}
                      >
                        <CheckMarkSVG size={16} color="#10B981" /> Video
                        Completed
                      </div>
                    )}

                    {isMobile && (
                      <div style={{ marginTop: '16px' }}>
                        <button
                          onClick={() =>
                            setChaptersCollapsed(!chaptersCollapsed)
                          }
                          style={{
                            width: '100%',
                            backgroundColor: '#FFFFFF',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                            borderRadius: '12px',
                            padding: '12px 16px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: chaptersCollapsed ? '0' : '12px',
                          }}
                        >
                          <span>Video Chapters</span>
                          <span style={{ fontSize: '12px' }}>
                            {chaptersCollapsed ? '▼' : '▲'}
                          </span>
                        </button>

                        {!chaptersCollapsed && (
                          <div
                            style={{
                              backgroundColor: '#F8F9FA',
                              borderRadius: '12px',
                              border: '1px solid #E5E7EB',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            }}
                          >
                            {videoChapters.map((chapter, index) => {
                              const canAccess =
                                sessionCompleted ||
                                chapter.timestamp <= playedSeconds;
                              const isActive = currentChapter === index;

                              return (
                                <div
                                  key={chapter.id}
                                  onClick={() =>
                                    canAccess &&
                                    jumpToChapter(chapter.timestamp)
                                  }
                                  style={{
                                    backgroundColor: isActive
                                      ? '#EFF6FF'
                                      : '#FFFFFF',
                                    borderRadius: '8px',
                                    border: isActive
                                      ? '2px solid #1B365D'
                                      : '1px solid #E5E7EB',
                                    padding: '12px',
                                    cursor: canAccess
                                      ? 'pointer'
                                      : 'not-allowed',
                                    opacity: canAccess ? 1 : 0.5,
                                    transition: 'all 0.2s ease',
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        backgroundColor: chapter.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: getTextColorForBackground(
                                          chapter.color,
                                        ),
                                        fontSize: '12px',
                                        border: '1px solid #E5E7EB',
                                        flexShrink: 0,
                                      }}
                                    >
                                      {canAccess ? (
                                        chapter.id
                                      ) : (
                                        <LockSVG
                                          size={12}
                                          color={getTextColorForBackground(
                                            chapter.color,
                                          )}
                                        />
                                      )}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <h4
                                        style={{
                                          fontSize: '13px',
                                          fontWeight: '600',
                                          color: '#374151',
                                          margin: '0 0 2px 0',
                                          lineHeight: '1.3',
                                        }}
                                      >
                                        {chapter.title}
                                        {!canAccess && (
                                          <span
                                            style={{
                                              color: '#6B7280',
                                              fontStyle: 'italic',
                                              opacity: 0.6,
                                            }}
                                          >
                                            {' '}
                                            (Locked)
                                          </span>
                                        )}
                                      </h4>
                                      <p
                                        style={{
                                          fontSize: '11px',
                                          color: '#6B7280',
                                          margin: 0,
                                          lineHeight: '1.3',
                                        }}
                                      >
                                        {chapter.description}
                                      </p>
                                    </div>

                                    <div
                                      style={{
                                        backgroundColor: '#F8F9FA',
                                        color: '#374151',
                                        padding: '2px 6px',
                                        borderRadius: '8px',
                                        fontSize: '10px',
                                        fontWeight: '600',
                                        border: '1px solid #E5E7EB',
                                      }}
                                    >
                                      {formatTime(chapter.timestamp)}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!isMobile && (
                  <div
                    style={{
                      backgroundColor: '#FFFFFF',
                      borderRadius: '16px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      padding: '24px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#374151',
                          margin: 0,
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                        }}
                      >
                        Video Chapters
                      </h3>
                      <button
                        onClick={() => setChaptersCollapsed(!chaptersCollapsed)}
                        style={{
                          backgroundColor: '#F8F9FA',
                          color: '#374151',
                          border: '1px solid #E5E7EB',
                          borderRadius: '12px',
                          padding: '8px 12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {chaptersCollapsed ? 'Show' : 'Hide'}
                        <span style={{ fontSize: '10px' }}>
                          {chaptersCollapsed ? '▼' : '▲'}
                        </span>
                      </button>
                    </div>

                    {!chaptersCollapsed && (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                        }}
                      >
                        {videoChapters.map((chapter, index) => {
                          // Can access if: video is completed OR chapter is at/before current position
                          const canAccess =
                            isVideoCompleted ||
                            chapter.timestamp <= playedSeconds;
                          const isActive = currentChapter === index;
                          return (
                            <div
                              key={chapter.id}
                              onClick={() =>
                                canAccess && jumpToChapter(chapter.timestamp)
                              }
                              style={{
                                backgroundColor: isActive
                                  ? '#EFF6FF'
                                  : '#FFFFFF',
                                borderRadius: '12px',
                                border: isActive
                                  ? '2px solid #1B365D'
                                  : '1px solid #E5E7EB',
                                padding: '16px',
                                cursor: canAccess ? 'pointer' : 'not-allowed',
                                opacity: canAccess ? 1 : 0.5,
                                transition: 'all 0.2s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (canAccess && !isActive) {
                                  e.currentTarget.style.backgroundColor =
                                    '#F8F9FA';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (canAccess && !isActive) {
                                  e.currentTarget.style.backgroundColor =
                                    '#FFFFFF';
                                }
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '16px',
                                }}
                              >
                                <div
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: chapter.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: getTextColorForBackground(
                                      chapter.color,
                                    ),
                                    fontSize: '16px',
                                    border: '1px solid #E5E7EB',
                                    flexShrink: 0,
                                  }}
                                >
                                  {canAccess ? (
                                    chapter.id
                                  ) : (
                                    <LockSVG
                                      size={12}
                                      color={getTextColorForBackground(
                                        chapter.color,
                                      )}
                                    />
                                  )}
                                </div>

                                <div style={{ flex: 1 }}>
                                  <h4
                                    style={{
                                      fontSize: '16px',
                                      fontWeight: '600',
                                      color: '#374151',
                                      margin: '0 0 4px 0',
                                    }}
                                  >
                                    {chapter.title}
                                    {!canAccess && (
                                      <span
                                        style={{
                                          color: '#6B7280',
                                          fontStyle: 'italic',
                                          opacity: 0.6,
                                        }}
                                      >
                                        {' '}
                                        (Locked)
                                      </span>
                                    )}
                                  </h4>
                                  <p
                                    style={{
                                      fontSize: '14px',
                                      color: '#6B7280',
                                      margin: 0,
                                    }}
                                  >
                                    {chapter.description}
                                  </p>
                                </div>

                                <div
                                  style={{
                                    backgroundColor: '#F8F9FA',
                                    color: '#374151',
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    border: '1px solid #E5E7EB',
                                  }}
                                >
                                  {formatTime(chapter.timestamp)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div
                style={{
                  order: isMobile ? 2 : 2,
                }}
              >
                <div
                  style={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                    padding: '24px',
                  }}
                >
                  <h3
                    style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 20px 0',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                    }}
                  >
                    Knowledge Check
                  </h3>

                  {!isVideoCompleted ? (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                      <div
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '50%',
                          backgroundColor: '#F8F9FA',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <LockSVG size={32} color="#374151" />
                      </div>
                      <h4
                        style={{
                          fontSize: '16px',
                          fontWeight: '600',
                          color: '#374151',
                          margin: '0 0 8px 0',
                        }}
                      >
                        Quiz Locked
                      </h4>
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#6B7280',
                          margin: '0 0 24px 0',
                        }}
                      >
                        Complete the video to unlock the quiz
                      </p>
                      <div
                        style={{
                          height: '8px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            backgroundColor: '#1B365D',
                            borderRadius: '4px',
                            width: `${(playedSeconds / videoDuration) * 100}%`,
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                      <p
                        style={{
                          fontSize: '12px',
                          color: '#6B7280',
                          marginTop: '8px',
                        }}
                      >
                        {Math.floor((playedSeconds / videoDuration) * 100)}%
                        complete
                      </p>
                    </div>
                  ) : quizCompleted && !showQuiz ? (
                    <div>
                      <div
                        style={{ textAlign: 'center', padding: '32px 0 24px' }}
                      >
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                          ✓
                        </div>
                        <h4
                          style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            color: '#374151',
                            margin: '0 0 8px 0',
                          }}
                        >
                          Quiz Completed!
                        </h4>
                        <p
                          style={{
                            fontSize: '14px',
                            color: '#6B7280',
                            margin: '0 0 24px 0',
                          }}
                        >
                          Great job completing the quiz!
                        </p>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                        }}
                      >
                        <button
                          onClick={() => setShowQuiz('review')}
                          style={{
                            backgroundColor: '#F8F9FA',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                            borderRadius: '24px',
                            padding: '12px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                          }}
                        >
                          <CheckMarkSVG size={14} color="#374151" /> Review
                          Answers
                        </button>
                      </div>

                        <div
                          style={{
                            marginTop: '24px',
                            padding: '16px',
                            backgroundColor: '#F8F9FA',
                            borderRadius: '12px',
                            border: '1px solid #E5E7EB',
                          }}
                        >
                          <div
                            style={{
                              textAlign: 'center',
                              marginBottom: '12px',
                            }}
                          >
                            <div
                              style={{ fontSize: '32px', marginBottom: '8px' }}
                            >
                              🎉
                            </div>
                            <h5
                              style={{
                                fontSize: '16px',
                                fontWeight: '600',
                                color: '#374151',
                                margin: '0 0 4px 0',
                              }}
                            >
                              Session Complete!
                            </h5>
                            <p
                              style={{
                                fontSize: '12px',
                                color: '#6B7280',
                                margin: '0 0 16px 0',
                              }}
                            >
                              Great job completing the quiz!
                            </p>
                          </div>

                          <h6
                            style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#374151',
                              margin: '0 0 4px 0',
                            }}
                          >
                            Next: Session {currentSessionNumber + 1}
                          </h6>
                          <p
                            style={{
                              fontSize: '12px',
                              color: '#6B7280',
                              margin: '0 0 16px 0',
                            }}
                          >
                            Managing Emotional Challenges in Orofacial Pain
                          </p>

                          {scheduledCountdown > 0 &&
                          nextSessionScheduledTime ? (
                            <div
                              style={{
                                backgroundColor: '#FEF3C7',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                border: '1px solid #F59E0B',
                                textAlign: 'center',
                                marginBottom: '12px',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '13px',
                                  color: '#92400E',
                                  fontWeight: '600',
                                  marginBottom: '2px',
                                }}
                              >
                                Scheduled unlock:{' '}
                                {getTimeUntilScheduledUnlock(
                                  nextSessionScheduledTime,
                                )}
                              </div>
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: '#B45309',
                                }}
                              >
                                {nextSessionScheduledTime.toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  },
                                )}
                              </div>
                            </div>
                          ) : countdown > 0 ? (
                            !nextSessionScheduledTime && (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '6px',
                                  fontSize: '12px',
                                  color: '#1B365D',
                                  backgroundColor: '#EFF6FF',
                                  padding: '8px 12px',
                                  borderRadius: '20px',
                                  border: '1px solid #3B82F6',
                                }}
                              >
                                Unlocks in: {Math.floor(countdown / 60)}:
                                {(countdown % 60).toString().padStart(2, '0')}
                              </div>
                            )
                          ) : nextSessionScheduledTime ? (
                            <button
                              onClick={() => navigateToNextSession()}
                              style={{
                                backgroundColor: '#1B365D',
                                color: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '24px',
                                padding: '12px 16px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                fontFamily:
                                  'system-ui, -apple-system, sans-serif',
                                width: '100%',
                              }}
                            >
                              Go to Next Session →
                            </button>
                          ) : (
                            <div
                              style={{
                                backgroundColor: '#F9FAFB',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                border: '1px solid #E5E7EB',
                                textAlign: 'center',
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                fill="#6B7280"
                                viewBox="0 0 24 24"
                                style={{ marginBottom: '8px' }}
                              >
                                <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
                              </svg>
                              <div
                                style={{
                                  fontSize: '13px',
                                  color: '#374151',
                                  fontWeight: '600',
                                  marginBottom: '2px',
                                }}
                              >
                                Next Session Locked
                              </div>
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: '#6B7280',
                                }}
                              >
                                Your research coordinator will schedule your
                                next session
                              </div>
                            </div>
                          )}
                        </div>
                    </div>
                  ) : showQuiz === 'review' ? (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '20px',
                        }}
                      >
                        <button
                          onClick={() => setShowQuiz(null)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            opacity: 0.8,
                          }}
                        >
                          ← Back
                        </button>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                          }}
                        >
                          Review Mode
                        </span>
                      </div>
                      <Quiz
                        showAnswers={true}
                        reviewMode={true}
                        requireCorrectAnswer={false}
                        onComplete={() => setShowQuiz(null)}
                        resetQuiz={false}
                      />
                    </div>
                  ) : showQuiz === 'start' ? (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '20px',
                        }}
                      >
                        <button
                          onClick={() => setShowQuiz(null)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            opacity: 0.8,
                          }}
                        >
                          ← Back
                        </button>
                        <span
                          style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#374151',
                          }}
                        >
                          Quiz
                        </span>
                      </div>
                      <Quiz
                        onComplete={handleLessonCompletion}
                        resetQuiz={false}
                        showAnswers={false}
                        reviewMode={false}
                        requireCorrectAnswer={true}
                      />
                    </div>
                  ) : (
                    <div>
                      <p
                        style={{
                          fontSize: '14px',
                          color: '#6B7280',
                          margin: '0 0 24px 0',
                        }}
                      >
                        Test your understanding of the video content.
                      </p>
                      <button
                        onClick={() => setShowQuiz('start')}
                        style={{
                          backgroundColor: '#EFF6FF',
                          color: '#1B365D',
                          border: '1px solid #3B82F6',
                          borderRadius: '24px',
                          padding: '12px 24px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          width: '100%',
                        }}
                      >
                        Start Quiz
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>

      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          input[type="range"] {
            -webkit-appearance: none;
            appearance: none;
            background: transparent;
            cursor: pointer;
          }

input[type="range"][orient="vertical"] {
  writing-mode: bt-lr; /* IE */
  -webkit-appearance: slider-vertical; /* Chromium */
  width: 4px;
  height: 100px;
  padding: 0;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  background: #FFFFFF;
  height: 14px;
  width: 14px;
  border-radius: 50%;
  border: 2px solid #3B82F6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

input[type="range"]::-moz-range-thumb {
  background: #FFFFFF;
  height: 14px;
  width: 14px;
  border-radius: 50%;
  border: 2px solid #3B82F6;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

          @media (max-width: 768px) {
            .video-overlay-controls {
              gap: 20px !important;
            }
            
            .video-overlay-controls button {
              width: 50px !important;
              height: 50px !important;
            }
/* Hide native video captions completely */
          video::cue {
            visibility: hidden !important;
            display: none !important;
          }
          video::-webkit-media-text-track-display {
            display: none !important;
          }
          video::-webkit-media-text-track-container {
            display: none !important;
          }
        `,
        }}
      />
    </div>
  );
}

export default ModernSessionPage;